import React, { useCallback } from 'react';
import {
  Button,
  ChevronRightIcon,
  Fact,
  LedgerHoverCard,
  LedgerRow,
  Money,
  StatusDot,
  formatLedgerWhen,
  formatLedgerWhenFull,
  statusToken,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import { formatCurrency } from '../../../shared/utils/currency';
import type { DeductionGroupingMode } from '../stores/deductionsUIStore';
import type { AdminDeductionRow } from '@billda/api';

export interface DeductionListItemProps {
  deduction: AdminDeductionRow;
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  groupingMode: DeductionGroupingMode;
  onClick?: (deduction: AdminDeductionRow) => void;
  'data-testid'?: string;
}

export const DeductionListItem = React.memo(function DeductionListItem({
  deduction,
  columns,
  collapse,
  groupingMode,
  onClick,
  'data-testid': dataTestId,
}: DeductionListItemProps) {
  const handleClick = useCallback(
    () => onClick?.(deduction),
    [deduction, onClick]
  );
  const isCancelled = deduction.status === 'Cancelled';
  const sitePlot = `${deduction.siteName ?? 'No site'} · ${deduction.plotReference ?? 'No plot'}`;
  const context =
    groupingMode === 'worker' ? sitePlot : deduction.workerOrGangName;
  const meta =
    groupingMode === 'none'
      ? `${deduction.status} · ${deduction.workerOrGangName} · ${sitePlot}`
      : `${deduction.status} · ${context}`;
  const createdAt = formatLedgerWhen(deduction.createdAt);
  const createdAtFull = formatLedgerWhenFull(deduction.createdAt);
  const amount = formatCurrency(deduction.amount);

  return (
    <LedgerRow
      columns={columns}
      collapse={collapse}
      lead={<StatusDot color={statusToken(deduction.status).color} />}
      title={deduction.description}
      cells={{
        context,
        status: deduction.status,
        sitePlot,
        createdBy: deduction.createdByName,
        when: createdAt,
      }}
      meta={meta}
      value={<Money value={deduction.amount} strike={isCancelled} />}
      dimmed={isCancelled}
      hoverCard={
        <LedgerHoverCard
          title={deduction.description}
          kind="Deduction"
          onOpenDetails={handleClick}
          openDetailsLabel="Open deduction details"
        >
          <Fact label="Status">{deduction.status}</Fact>
          <Fact label="Applied to">{deduction.workerOrGangName}</Fact>
          <Fact label="Site · Plot">{sitePlot}</Fact>
          <Fact label="Added by">{deduction.createdByName}</Fact>
          <Fact label="Added">{createdAtFull || 'Not available'}</Fact>
          {deduction.uniqueReference && (
            <Fact label="Reference">{deduction.uniqueReference}</Fact>
          )}
          <Fact label="Amount">{amount}</Fact>
        </LedgerHoverCard>
      }
      actions={
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClick}
          aria-label={`Open ${deduction.description} deduction`}
          className="size-8"
        >
          <ChevronRightIcon aria-hidden="true" className="size-4" />
        </Button>
      }
      onClick={handleClick}
      data-status={deduction.status}
      data-testid={dataTestId}
    />
  );
});
